import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";
import { CATEGORIES, IMAGES, waLink } from "../lib/site";

const FALLBACK_PRODUCTS = [
  {
    id: "oem-sensor",
    name: "حساس TPMS أصلي (وكالة)",
    category: "oem",
    sensorType: "حساس أصلي حسب مواصفات الشركة الصانعة",
    description:
      "حساسات أصلية مطابقة لمواصفات الوكالة حسب توفرها للموديل وسنة الصنع. يتم التأكد من التوافق والتردد قبل التوريد.",
    compatibility: "يُحدَّد التوافق بعد مراجعة بيانات السيارة (الموديل وسنة الصنع ونوع النظام).",
    image: IMAGES.sensorOem,
  },
  {
    id: "aftermarket-sensor",
    name: "حساس TPMS بديل عالي الجودة",
    category: "aftermarket",
    sensorType: "حساس بديل (Aftermarket)",
    description:
      "خيارات بديلة بجودة مجربة وبأسعار منافسة للوكالة، مع التأكد من دعم التردد والبروتوكول المطلوب لسيارتك.",
    compatibility: "يُحدَّد التوافق بعد مراجعة بيانات السيارة.",
    image: IMAGES.sensorAftermarket,
  },
  {
    id: "programmable-sensor",
    name: "حساس عالمي قابل للبرمجة",
    category: "programmable",
    sensorType: "حساس قابل للبرمجة (Programmable)",
    description:
      "حساسات متعددة البروتوكولات تُبرمَج لتتوافق مع نظام سيارتك عند توفر الدعم، حلاً عمليًا لكثير من الموديلات.",
    compatibility: "تُبرمج حسب السيارة بعد التحقق من دعم النظام.",
    image: IMAGES.sensorProgrammable,
  },
  {
    id: "valve-kit",
    name: "صمامات حساسات معدنية ومطاطية",
    category: "valves",
    sensorType: "صمام / عدد صيانة",
    description:
      "صمامات ألمنيوم ومطاطية وعدد صيانة (جلب وسيلات) لمعالجة تسريب الهواء من منطقة الصمام عند تغيير الكفرات أو الحساس.",
    compatibility: "تُختار حسب نوع الحساس والجنط.",
    image: IMAGES.valveCore,
  },
  {
    id: "tpms-tool",
    name: "أجهزة برمجة وفحص الحساسات",
    category: "tools",
    sensorType: "جهاز فحص وبرمجة TPMS",
    description:
      "أجهزة متخصصة لقراءة حالة الحساس وبطاريته وتردده وبرمجة الحساسات القابلة للبرمجة وإجراء إعادة التعلم (Relearn).",
    compatibility: "تغطي أغلب الأنظمة الشائعة حسب نوع الجهاز.",
    image: IMAGES.diagTool,
  },
  {
    id: "oem-sensor-2",
    name: "حساسات أنظمة الضغط للدفع الرباعي",
    category: "oem",
    sensorType: "حساس أصلي / بديل حسب التوفر",
    description:
      "حلول حساسات لسيارات الدفع الرباعي الشائعة في السعودية مع مراعاة اختلاف الترددات بين الفئات والموديلات.",
    compatibility: "يُحدَّد التوافق بعد مراجعة بيانات السيارة.",
    image: IMAGES.sensorInTire,
  },
];

export const ProductCatalog = () => {
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return;
    fetch(`${url}/api/products`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (Array.isArray(data) && data.length) setProducts(data);
      })
      .catch(() => {});
  }, []);

  const filtered = tab === "all" ? products : products.filter((p) => p.category === tab);

  return (
    <section
      id="catalog"
      data-testid="catalog-section"
      className="bg-[#0A0C11] py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Catalog"
          title="معرض الحساسات"
          desc="فئات الحساسات والملحقات التي نتعامل معها. الصور توضيحية ولا تمثل بالضرورة المخزون الفعلي، ولا تُعرض الأسعار على الموقع — استفسر عبر واتساب."
        />

        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`catalog-tab-${c.id}`}
              onClick={() => setTab(c.id)}
              className={`text-xs sm:text-sm px-4 py-2 rounded-full border transition-all ${
                tab === c.id
                  ? "bg-red-600 border-red-500 text-white font-semibold"
                  : "bg-white/5 border-white/10 text-slate-300 hover:border-red-500/40"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button
                layout
                key={p.id}
                type="button"
                data-testid={`catalog-item-${p.id}`}
                onClick={() => setSelected(p)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="text-right metal-card rounded-2xl overflow-hidden group hover:border-red-500/40 transition-colors"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={p.image}
                    alt={`صورة توضيحية — ${p.name}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c0e14] via-transparent to-transparent" />
                  <span className="absolute top-3 right-3 text-[10px] font-mono tracking-widest bg-black/60 border border-white/15 rounded-full px-3 py-1 text-slate-300">
                    {CATEGORIES.find((c) => c.id === p.category)?.label}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-white text-base mb-1.5">{p.name}</h3>
                  <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">{p.description}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="catalog-item-detail-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#101218] border border-white/10 rounded-t-3xl sm:rounded-3xl w-full max-w-lg overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-label={selected.name}
            >
              <div className="relative h-52">
                <img
                  src={selected.image}
                  alt={`صورة توضيحية — ${selected.name}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#101218] to-transparent" />
                <button
                  type="button"
                  data-testid="catalog-modal-close"
                  onClick={() => setSelected(null)}
                  aria-label="إغلاق"
                  className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/60 border border-white/15 text-white flex items-center justify-center hover:border-red-500/60"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-display text-xl font-bold text-white mb-1">{selected.name}</h3>
                <p className="font-mono text-[11px] text-red-400 mb-4">{selected.sensorType}</p>
                <p className="text-sm text-slate-300 leading-relaxed mb-3">{selected.description}</p>
                <p className="text-xs text-slate-500 leading-relaxed border border-white/10 rounded-xl p-3 bg-white/[0.03] mb-6">
                  {selected.compatibility}
                </p>
                <a
                  href={waLink(`السلام عليكم، أرغب في الاستفسار عن: ${selected.name}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="catalog-modal-whatsapp-cta"
                  className="w-full inline-flex items-center justify-center gap-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-3.5 rounded-2xl transition-all active:scale-[0.98]"
                >
                  <MessageCircle size={18} />
                  استفسر عبر واتساب
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
